import { useEffect } from 'react';
import { createRootRoute, Outlet } from '@tanstack/react-router';
import { AlertHost } from '@/components/alerts/AlertHost';
import { Toaster } from '@/components/alerts/Toaster';
import { CommandPalette } from '@/components/layout/CommandPalette';
import { CriticalAlertFlash } from '@/components/layout/CriticalAlertFlash';
import { KeyboardShortcutsOverlay } from '@/components/layout/KeyboardShortcuts';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { LoginScreen } from '@/components/auth/LoginScreen';
import { IconRail } from '@/components/layout/IconRail';
import { TopBar } from '@/components/layout/TopBar';
import { LogViewer } from '@/components/debug/LogViewer';
import { UpdateDialog } from '@/components/updater/UpdateDialog';
import { useHubAuditPoll } from '@/hooks/useHubAuditPoll';
import { useAuth } from '@/store/auth';
import { useHubSocket } from '@/hooks/useHubSocket';
import { useManualControl } from '@/hooks/useManualControl';
import { installMissionUploadAckBridge } from '@/store/missionUpload';
import { useUpdater } from '@/store/updater';
import { getSoloMode } from '@/lib/tauri';
import { SoloView } from '@/components/layout/SoloView';

function RootLayout() {
  const token = useAuth((s) => s.token);
  const login = useAuth((s) => s.login);
  const rehydrate = useAuth((s) => s.rehydrate);
  // Validate persisted token against the Hub at app boot.
  // Also: if VITE_AUTH_BYPASS is set (dev convenience), auto-sign-in as the
  // configured user/password. NEVER set this in production.
  useEffect(() => {
    rehydrate();
    installMissionUploadAckBridge();
    // Check for app updates once at boot. Branches per platform internally —
    // desktop opens the dialog; Android pushes a toast; web is a no-op.
    void useUpdater.getState().init();
    const bypass = import.meta.env.VITE_AUTH_BYPASS;
    if (bypass && !useAuth.getState().token) {
      // bypass format: "username:password" — defaults to admin:password.
      const [u, p] = typeof bypass === 'string' && bypass.includes(':')
        ? bypass.split(':')
        : ['admin', 'password'];
      void login(u!, p!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hooks below are unconditional so React's hook order rules are respected;
  // they exit early internally when no token is present.
  useHubSocket();
  useManualControl();
  useHubAuditPoll();

  if (!token) {
    return <LoginScreen />;
  }

  const solo = getSoloMode();
  if (solo) {
    return (
      <div className="flex h-full flex-col">
        <ErrorBoundary label="Solo view crashed">
          <SoloView mode={solo} />
        </ErrorBoundary>
        <AlertHost />
        <Toaster />
      </div>
    );
  }
  return (
    <div className="flex h-full flex-col">
      <TopBar />
      <div className="flex flex-1 overflow-hidden">
        <IconRail />
        <main className="relative flex-1 overflow-hidden">
          <ErrorBoundary label="Workspace crashed">
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
      <AlertHost />
      <Toaster />
      <UpdateDialog />
      <LogViewer />
      <CommandPalette />
      <KeyboardShortcutsOverlay />
      <CriticalAlertFlash />
    </div>
  );
}

export const rootRoute = createRootRoute({
  component: RootLayout,
});
